import { IonItem, IonItemOption, IonItemOptions, IonItemSliding, IonLabel, IonNote, IonText } from '@ionic/react';
import { useMemo, useRef } from 'react';

import { Icon } from '@/components/Icon';
import { Indicator } from '@/components/Indicator/Indicator';
import { Tag } from '@/components/Tag/Tag';

import { mergeClassNames } from '@/utils';

import { Recording } from '@/store/ActiveSessionStore';

import styles from './RecordingItem.module.css';

import { MovementType } from '@/types/MovementTypes';

interface RecordingItemProps {
  recording: Recording;
  handleDeleteClick: VoidFunction;
  handleTagClick: VoidFunction;
}

export const RecordingItem = ({ recording, handleDeleteClick, handleTagClick }: RecordingItemProps) => {
  const isActive = recording.state === 'active';
  const isRecording = recording.state === 'recording';

  const slidingElementRef = useRef<HTMLIonItemSlidingElement>(null);

  // formatted time as 2 digit hours and 2 digit seconds
  const formattedTime = useMemo(() => {
    const date = new Date(recording.time);
    const hours = date.getHours();
    const minutes = date.getMinutes();

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }, [recording.time]);

  // take only the last two digits of the recording id, and remove preceding zeros
  const formattedId = useMemo(() => Number.parseInt(recording.id.toString().slice(-2)).toString(), [recording.id]);

  const title = useMemo(() => {
    switch (recording.movementType) {
      case MovementType.walk:
        return 'Walk';
      case MovementType.run:
        return 'Run';
      case MovementType.singleLegSquat:
        return 'Single Leg Squat';
      case MovementType.doubleLegSquat:
        return 'Double Leg Squat';
      default:
        return 'Unknown';
    }
  }, [recording.movementType]);

  const iconForMovementType = useMemo(() => {
    switch (recording.movementType) {
      case MovementType.doubleLegSquat:
        return 'squat';
      case MovementType.run:
        return 'run';
      case MovementType.singleLegSquat:
        return 'singleLegSquat';
      default:
        return 'walk';
    }
  }, [recording.movementType]);

  const handleOptionClick = (option: 'delete' | 'tag') => () => {
    if (slidingElementRef.current) {
      slidingElementRef.current.close();
    }

    switch (option) {
      case 'delete':
        handleDeleteClick();
        break;
      case 'tag':
        handleTagClick();
        break;
    }
  };

  return (
    <IonItemSliding
      className={mergeClassNames(styles.sliding, isActive && styles.active, isRecording && styles.recording)}
      ref={slidingElementRef}>
      <IonItem className={styles.item}>
        <IonLabel className={styles.label}>
          <Icon
            className={styles.icon}
            color={isRecording || isActive ? 'var(--ion-color-grey400)' : 'var(--ion-color-grey300)'}
            height={24}
            name={iconForMovementType}
            width={16}
          />
          <div className={styles.labelContent}>
            <IonText className={styles.labelTextContent}>{title}</IonText>
            {!isRecording && !isActive && recording.tags && recording.tags?.length > 0 && (
              <div className={styles.tags}>
                {recording.tags.map((tag) => (
                  <Tag key={tag.id} variant='measurement'>
                    {tag.label}
                  </Tag>
                ))}
              </div>
            )}
          </div>
        </IonLabel>

        {!isActive && !isRecording && (
          <IonNote className={styles.note} slot='end'>
            <IonText className={styles.id} title={recording.id.toString()}>
              #{formattedId}
            </IonText>
            <div className={styles.spacer} />
            <IonText className={styles.time}>{formattedTime}</IonText>
          </IonNote>
        )}

        {isRecording && <Indicator className={styles.indicator} color='var(--ion-color-teal500)' />}
      </IonItem>

      {!isActive && !isRecording && (
        <IonItemOptions className={styles.options}>
          <IonItemOption className={styles.delete} onClick={handleOptionClick('delete')}>
            <Icon color='#E7364B' name='delete' />
          </IonItemOption>
          <IonItemOption className={styles.tag} onClick={handleOptionClick('tag')}>
            <Icon color='var(--ion-color-grey700)' name='tag' />
          </IonItemOption>
        </IonItemOptions>
      )}
    </IonItemSliding>
  );
};
